import React from 'react';
import { BonusGameType } from '../../../constants/bonusConstants';
import type { BonusLevelPayload } from '../../../constants/bonusConstants';
import { getBonusIntro } from '../../../i18n/helpers';
import { FastFingersGuide } from './fastFingers/FastFingersGuide';
import '../../../styles/bonus-intro.css';
import { useTranslation } from '../../../i18n';

interface BonusIntroProps {
  bonusGameType: BonusGameType;
  /** When true, show "Last round, last chance to win!" instead of "Bonus Round". */
  isLastRound?: boolean;
  /** Optional payload: for catch-me, used to show pack-specific intro title/description when present. */
  payload?: BonusLevelPayload | null;
}

/**
 * Reusable intro screen for bonus levels: big "Bonus Round" (or "Last round, last chance to win!" for final bonus) with blink, then game-specific title/description.
 */
export const BonusIntro: React.FC<BonusIntroProps> = ({ bonusGameType, isLastRound, payload }) => {
  const { t } = useTranslation();
  const defaultIntro = getBonusIntro(bonusGameType);
  const isCatchMe = bonusGameType === 'catch-me-if-you-can';
  const usePayloadIntro = isCatchMe && payload?.introTitle != null;
  const intro: { title: string; description: string; detail?: string } = usePayloadIntro
    ? {
        title: payload!.introTitle!,
        description: payload!.introDescription ?? '',
        detail: typeof payload?.introDetail === 'string' ? payload.introDetail : undefined,
      }
    : defaultIntro;

  const titleClass = isLastRound ? 'bonus-intro-title bonus-intro-title-last' : 'bonus-intro-title';
  const titleText = isLastRound ? t('bonus.lastRoundTitle') : t('bonus.roundTitle');

  const showFastFingersGuide =
    bonusGameType === 'fast-fingers' &&
    payload?.successEmoji &&
    Array.isArray(payload.wrongEmojis) &&
    payload.wrongEmojis.length > 0;

  return (
    <div
      style={{
        textAlign: 'center',
        padding: showFastFingersGuide ? '1rem 1.5rem 0' : '1.5rem',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        flex: showFastFingersGuide ? '1 1 auto' : undefined,
        minHeight: 0,
      }}
    >
      <h1 className={titleClass} style={{ marginBottom: showFastFingersGuide ? '0.75rem' : '1.25rem' }}>
        {titleText}
      </h1>
      {intro && (
        <>
          <h2 className="bonus-intro-game-title" style={{ marginBottom: showFastFingersGuide ? '0.35rem' : undefined }}>
            {intro.title}
          </h2>
          {intro.description && (
            <p className="bonus-intro-description" style={{ marginBottom: intro.detail ? '0.35rem' : undefined }}>
              {intro.description}
            </p>
          )}
          {intro.detail && (
            <p className="bonus-intro-detail">
              {intro.detail}
            </p>
          )}
        </>
      )}
      {showFastFingersGuide && (
        <FastFingersGuide
          compact
          successEmoji={payload!.successEmoji}
          wrongEmojis={payload!.wrongEmojis}
          successPoints={payload!.successPoints ?? 10}
          wrongPenalty={payload!.wrongPenalty ?? 5}
        />
      )}
    </div>
  );
};
